import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchEvidenceFile } from '../services/api';
import { statusFromColor } from '../utils/progressStatus';
import { dataPointTypeLabel } from '../utils/dataPointType';
import { pointColor } from '../utils/grading';
import AdditionalSupportIndicator, { ADDITIONAL_SUPPORT_COLOR, ADDITIONAL_SUPPORT_LABEL } from './AdditionalSupportIndicator';

const STATUS_LABELS = { mastered: 'Proficient', on_track: 'On Track', working: 'Learning Zone', not_yet: 'Not Yet', na: 'Not Assessed' };

export function EvidenceAttachment({ attachment, dataPointId, linkColor = '#bfdbfe', errorColor = '#fecaca' }: any) {
  const [url, setUrl] = useState<any>('');
  const [loading, setLoading] = useState<any>(false);
  const [error, setError] = useState<any>('');
  const mounted = useRef(true);
  const objectUrl = useRef('');

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    };
  }, []);

  const loadEvidence = async () => {
    setLoading(true);
    setError('');
    try {
      const blob = await fetchEvidenceFile(attachment.filename, dataPointId);
      if (!mounted.current) return;
      objectUrl.current = URL.createObjectURL(blob);
      setUrl(objectUrl.current);
    } catch (loadError: any) {
      if (mounted.current) setError(loadError.message || 'Could not load evidence.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 6, overflowWrap: 'anywhere' }}>
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: linkColor }}>
          Open {attachment.name}
        </a>
      ) : (
        <button type="button" disabled={loading} onClick={loadEvidence} style={{ cursor: 'pointer' }}>
          {loading ? 'Loading evidence...' : `View ${attachment.name}`}
        </button>
      )}
      {error && <div role="alert" style={{ color: errorColor, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

export function collectDataPointHistory(strands, useFamilyDescriptions = true) {
  const points = new Map();
  strands.forEach((strand) => strand.clusters.forEach((cluster) => cluster.standards.forEach((standard) => {
    (standard.history || []).forEach((point, index) => {
      const key = point.id != null ? point.id : `${standard.standardCode}|${index}`;
      if (!points.has(key)) points.set(key, {
        ...point, standardCode: standard.standardCode,
        standardDescription: (useFamilyDescriptions && standard.familyDescription) || standard.detail || standard.label,
      });
    });
  })));
  return [...points.values()].sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')) || (a.id || 0) - (b.id || 0));
}

export default function DataPointDots({ history, getColor, hideScore, showPrivateNotes = false, label = 'Standard data points' }: any) {
  const [active, setActive] = useState<any>(null);
  const popupRef = useRef<any>(null);
  const triggerRef = useRef<any>(null);
  const closeTimer = useRef<any>(null);

  useEffect(() => {
    if (!active) return undefined;
    if (active.focusPopup) popupRef.current?.querySelector('button')?.focus();
    const closeOutside = (event) => {
      if (!popupRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) {
        setActive(null);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setActive(null);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      clearTimeout(closeTimer.current);
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [active]);

  if (!history?.length) return null;
  const keepOpen = () => clearTimeout(closeTimer.current);
  const closeAfterHover = () => {
    if (!active?.focusPopup) closeTimer.current = setTimeout(() => setActive(null), 300);
  };
  const openFor = (event, point) => {
    event.stopPropagation();
    keepOpen();
    triggerRef.current = event.currentTarget;
    const rect = event.currentTarget.getBoundingClientRect();
    setActive({ point, x: rect.left, y: rect.bottom, focusPopup: event.type === 'click' });
  };
  const status = active ? statusFromColor(pointColor(active.point)) : null;
  const canViewContent = active && (active.point.notes_visibility === 'shared' || showPrivateNotes);
  const attachments = canViewContent ? [...(active.point.evidence_files || [])] : [];
  if (canViewContent && active.point.evidence && !attachments.some((item) => item.filename === active.point.evidence)) {
    attachments.unshift({ filename: active.point.evidence, name: 'uploaded evidence' });
  }

  return (
    <div role="group" aria-label={label} onMouseLeave={closeAfterHover} style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', marginBottom: 4 }}>
      {history.map((point, index) => (
        <button
          type="button"
          key={point.id || index}
          aria-label={`Data point ${index + 1}${point.date ? ` on ${point.date}` : ''}${point.accommodations_applied ? `, ${ADDITIONAL_SUPPORT_LABEL}` : ''}`}
          aria-haspopup="dialog"
          aria-expanded={active?.point === point}
          onMouseEnter={(event) => openFor(event, point)}
          onClick={(event) => openFor(event, point)}
          style={{ width: 14, height: 14, padding: 0, borderRadius: '50%', background: getColor(statusFromColor(pointColor(point))), border: '1.5px solid rgba(255,255,255,0.95)', boxShadow: point.accommodations_applied ? `0 0 0 2px ${ADDITIONAL_SUPPORT_COLOR}` : '0 0 0 1px rgba(15,23,42,0.25)', cursor: 'pointer' }}
        />
      ))}
      {active && createPortal(
        <div
          ref={popupRef}
          role="dialog"
          aria-label="Data point details"
          className={active.point.accommodations_applied ? 'ae-supported-data-point' : undefined}
          onClick={(event) => event.stopPropagation()}
          onMouseEnter={keepOpen}
          onMouseLeave={closeAfterHover}
          style={{
            position: 'fixed', left: Math.max(8, Math.min(active.x, window.innerWidth - 296)),
            top: Math.max(8, Math.min(active.y + 6, window.innerHeight - 220)),
            zIndex: 1000, width: 'min(280px, calc(100vw - 32px))', maxHeight: 'min(360px, calc(100vh - 32px))',
            overflowY: 'auto', background: '#1e293b', color: '#f8fafc', borderRadius: 10, padding: 12,
            fontSize: 13, lineHeight: 1.5, boxShadow: '0 8px 20px rgba(0,0,0,0.25)', textAlign: 'left',
          }}
        >
          <div className="ae-data-point-header">
            <div style={{ fontWeight: 800 }}>{active.point.title || 'Data point'}</div>
            <AdditionalSupportIndicator applied={active.point.accommodations_applied} />
          </div>
          <button type="button" aria-label="Close data point details" onClick={() => { setActive(null); triggerRef.current?.focus(); }} style={{ float: 'right', cursor: 'pointer' }}>Close</button>
          <div>Type: {dataPointTypeLabel(active.point.data_type)}</div>
          {active.point.standardDescription && <div style={{ marginTop: 6 }}>{active.point.standardDescription}</div>}
          {active.point.standardCode && <div style={{ fontSize: 11 }}>{active.point.standardCode}</div>}
          {active.point.date && <div>Date: {active.point.date}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: getColor(status), display: 'inline-block' }} />
            {STATUS_LABELS[status]}{!hideScore && active.point.score != null && ` · ${active.point.score}%`}
          </div>
          {active.point.notes && canViewContent && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 700 }}>{active.point.notes_visibility === 'shared' ? 'Shared notes' : 'Private notes (staff only)'}</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{active.point.notes}</div>
            </div>
          )}
          {attachments.length > 0 && <div style={{ marginTop: 8, fontWeight: 700 }}>Evidence</div>}
          {attachments.map((attachment) => (
            <EvidenceAttachment key={`${active.point.id}|${attachment.filename}`} attachment={{ ...attachment, name: attachment.name || 'uploaded evidence' }} dataPointId={active.point.id} />
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
